<template>
    <section v-if="track" class="track_notes_panel">
        <textarea
            class="note_content"
            v-model="noteText"
            :placeholder="$t('notes.placeholder')"
            @input="scheduleNoteSave"
            @blur="saveNote"
        ></textarea>
        <div class="note_actions mt-3">
            <span v-if="isSavingNote" class="note_save_status">{{ $t('notes.saving') }}</span>
            <span v-else-if="noteSaveError" class="note_save_error">{{ noteSaveError }}</span>
        </div>
    </section>
</template>

<script>
import storageService from '@/scripts/services/StorageService';

export default {
    name: 'TrackNotesPanel',
    props: {
        track: {
            type: Object,
            default: null
        }
    },
    emits: ['updated'],
    data() {
        return {
            noteText: '',
            isSavingNote: false,
            noteSaveError: '',
            noteSaveTimeout: null
        };
    },
    watch: {
        track: {
            handler(newTrack, oldTrack) {
                this.syncTrackNotes(newTrack, oldTrack);
            },
            immediate: true
        }
    },
    beforeUnmount() {
        if (this.noteSaveTimeout) {
            window.clearTimeout(this.noteSaveTimeout);
        }
    },
    methods: {
        syncTrackNotes(newTrack, oldTrack = null) {
            if (!newTrack) {
                this.noteText = '';
                return;
            }

            const trackChanged = newTrack.id !== oldTrack?.id;
            const previousNotes = oldTrack?.notes ?? '';
            const localMatchesPrevious = this.noteText === previousNotes;
            if (trackChanged || (!this.isSavingNote && !this.noteSaveTimeout && localMatchesPrevious)) {
                this.noteText = newTrack.notes ?? '';
            }
        },
        scheduleNoteSave() {
            this.noteSaveError = '';
            if (this.noteSaveTimeout) {
                window.clearTimeout(this.noteSaveTimeout);
            }
            this.noteSaveTimeout = window.setTimeout(() => {
                this.noteSaveTimeout = null;
                this.saveNote();
            }, 600);
        },
        async saveNote() {
            if (!this.track) return;

            if (this.noteSaveTimeout) {
                window.clearTimeout(this.noteSaveTimeout);
                this.noteSaveTimeout = null;
            }

            if (this.isSavingNote) return;

            const notes = this.noteText.trim();
            const previousNotes = this.track.notes ?? '';
            if (notes === previousNotes) {
                this.noteText = notes;
                return;
            }

            this.isSavingNote = true;
            this.noteSaveError = '';
            const updatedTrack = { ...this.track, notes };
            const saved = await storageService.updateTrack(updatedTrack);
            let savedNotes = previousNotes;

            if (saved) {
                const savedTrack = storageService.getTrackById(updatedTrack.id) || updatedTrack;
                savedNotes = savedTrack.notes ?? notes;
                this.$emit('updated', savedTrack);
            } else if (this.noteText === notes) {
                this.noteText = previousNotes;
                this.noteSaveError = this.$t('notes.saveError');
            }

            this.isSavingNote = false;
            if (this.noteText.trim() !== savedNotes) {
                this.scheduleNoteSave();
            }
        }
    }
};
</script>

<style scoped>
.track_notes_panel {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.note_content {
  display: block;
  width: 100%;
  min-height: 12rem;
  padding: 0.75rem;
  resize: vertical;
  color: inherit;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  outline: none;
}

.note_content::placeholder {
  color: #888;
}

.note_content:focus {
  border-color: rgba(255, 255, 255, 0.5);
}

.note_actions {
  min-height: 1.25rem;
  font-size: 0.875rem;
}

.note_save_status {
  color: #aaa;
}

.note_save_error {
  color: #ff8e8e;
}
</style>
